import { makeAutoObservable, runInAction } from "mobx";
import { request } from "@/common";
import client from "@/store/client";
import { HelperCore } from "@/common/dependencies";
import slsLogger from "@public/log/sls";
import { isAgentFlowId, McpServerInstallStatus } from "@shared/constants/mcp";
import Other from "@/common/helper/other";
import globalStore from "@/store/global";
import clientMcp from "@/store/clientMcp";
import { isEmpty } from "lodash-es";

const Install_Timeout = 30000

class ChatAgent {
  /** SayList 列表 */
  sayList = []
  /** 技能安装列表,  */
  serverList = []
  /** 标识：所有技能是否已安装 */
  installed = false
  /** 智能体详情 */
  agentInfo = {}

  confirmModal = null;

  constructor() {
    makeAutoObservable(this);

    if(!HelperCore.IS_PROD){
      window._chatAgent = this;
    }
  }

  /** 初始化：获取智能体详情 */
  async initAgentInfo(agentId) {
    const isAgentFlow = isAgentFlowId(agentId);

    // 在初始化过程中获取智能体详情
    // 取出详情中的 技能列表 和 提示词列表
    (
      isAgentFlow
      ? request('/ai_flow/flow_info', {
        method: 'get',
        params: {
          flow_id: agentId,
        }
      })
      : request('/ai_agent/detail', {
        method: 'get',
        params: {
          id: agentId,
        }
      })
    )
    .then(res => {
      if (res.code === 0) {
        if (isEmpty(res.data)) {
          Other.toast('抱歉，该智能体不存在或已被删除', 'warning')
          globalStore.navigate('/tools/aiagent')
          return
        }
      } else if (res.code === 100004) {
        Other.toast('抱歉，您没有该智能体的访问权限', 'warning')
        globalStore.navigate('/tools/aiagent')
        return
      } else if (isEmpty(res.data)) {
        Other.toast('抱歉，该智能体不存在或已被删除', 'warning')
        globalStore.navigate('/tools/aiagent')
        return
      }

      // if(!res?.data) {
      //   Other.toast("该智能体已被下架", "warning")
      //   setTimeout(() => {
      //     globalStore.reloadApp()
      //   }, 2000)
      //   return
      // }
      // debug 自带默认 prompt
      // if(HelperCore.IS_LOCAL){
      //   res.data.doc_def_prompt = "总结一下";
      // }

      runInAction(() => {
        this.agentInfo = res.data || {}
        this.agentInfo.prompt_form = this.agentInfo.prompt_form || []

        this.sayList = this.agentInfo.saylist_detail || []

        this.serverList = (this.agentInfo.server_list || []).map(server => {
          // 如果是云端MCP服务，默认安装成功
          if (server.is_local === 1) {
            const status = client.supportMcpAgent ? McpServerInstallStatus.unInstalled : McpServerInstallStatus.failed
            return { ...server, status }
          }
          return {...server, status: 2 }
        })
        // TODO 这一部分逻辑和安装交互使用
        // web/src/pages/chat/components/agent-install/index.jsx
        this.startInstall();
      })
    }).catch(err => {
      if(err?.code == 500) {
        Other.toast("该智能体已被下架", "warning")
        setTimeout(() => {
          globalStore.hrefNavigate(HelperCore.repaireUrl("/tools/aiagent"));
          return
        }, 2000)
      }
      if (err?.code == 100004) {
        Other.toast('抱歉，您没有该智能体的访问权限', 'warning')
        globalStore.navigate('/tools/aiagent')
      } else if (isEmpty(err?.data)) {
        Other.toast('抱歉，该智能体不存在或已被删除', 'warning')
        globalStore.navigate('/tools/aiagent')
      }
    })
  }

  /** 退出中间页后一定要重置 */
  reset() {
    this.sayList = []
    this.serverList = []
    this.installed = false
    this.agentInfo = {}
  }

  async check360SeSupportMcp() {
    return new Promise((resolve, reject) => {
      client.run("IsSupportMcp", (data) => {
        if (data?.succ === true) {
          resolve(true)
        } else {
          resolve(false)
        }
      }, '', '', '360Mcp');
    })
  }

  startInstall = async () => {
    if (this.serverList.length < 1) {
      runInAction(() => {
        this.installed = true
      });
      return
    }
    // const server_names = this.serverList.map(server => server.server_name)
    const server_names = this.serverList
      .filter(server => server.is_local === 1)
      .map(server => server.server_name)
    // 如果 server_names 为空，说明无本地服务并且已安装完毕（到这一步可这么认为)
    let is360SeSupportMcp = true;
    if (client.isSeLLQ) {
      is360SeSupportMcp = await this.check360SeSupportMcp()
    }
    if (!is360SeSupportMcp || (server_names.length && !client.supportMcpAgent)) {
      const tips = client.isLLQ ? '请升级至最新版,体验完整功能' : '前往下载页面安装最新客户端,体验完整功能';
      const confirmModal = Other.confirm({
        title: '温馨提示',  
        content: tips,
        onOk: () => {
          client.openPage('/download');
        },
      });
      this.confirmModal = confirmModal;
      return;
    }
    if (server_names.length && client.notSupportWin7) {
      const tips = '此MCP不支持在Windows 7系统下运行，建议您升级系统后体验'
      const confirmModal = Other.confirm({
        title: '温馨提示',
        content: tips,
      })
      this.confirmModal = confirmModal
      return
    }

    console.info('>>> 正在安装 MCP 服务列表：', server_names.join(','))

    const beginTimeStatus = +new Date;
    let statusRes = {data: '如果没有内容：可能McpQuery出错了'}
    try{
      statusRes = await client.proxyRequestAsyncWithTimeout("McpQuery", {
        api : `/api/server/status`,
        method : 0,
        timeout : 1000,  // 加超时不然会被上报
        param : {
          server_names,
        }
      }, "", "360Mcp");
      // console.info('>>> McpQuery 阶段安装完成', statusRes)
      const serverList = JSON.parse(statusRes.data).data;
      // 如果 serverList 不存在，上报错误：浏览器端改接口返回了
      if (!serverList) {
        slsLogger.log({
          key : "client",
          action : "status_install_error",
          arrt : "McpQuery",
          count : +new Date - beginTimeStatus,
          ext : statusRes.data,
          ext2 : JSON.stringify(server_names),
        })
      }
      
      // 当 server_names 为空，这里可能是 udnefined
      // Array.isArray(serverList) && serverList.forEach(item => {
      //   if(item.installed){
      //     clientMcp.setMcpInstallCache(item.server_name);
      //   }
      // })
      if(Array.isArray(serverList) && serverList.length == server_names.length && serverList.every(item => item.installed)){
        runInAction(() => {
          console.info('>>> McpQuery 阶段安装完成')
          this.installed = true;
        });
        return;
      }
    } catch(e){
      slsLogger.log({
        key : "client",
        action : "status_timeout",
        arrt : "McpQuery",
        count : +new Date - beginTimeStatus,
        ext : e.stack || e.toString(),
        ext2 : JSON.stringify(server_names),
        ext3 : JSON.stringify(this.serverList),
        ext4 : statusRes.data,
      });
    }

    const beginTime = +new Date;
    const timeout = 60000;
    Promise.all(server_names.map(server_name => {
      return new Promise((resolve) => {
        let hasTimeout = false;
        const timer = setTimeout(() => {
          hasTimeout = true;
          const server = this.serverList.find(item => item.server_name == server_name);
          if (!server) return
          runInAction(() => {
            server.status = McpServerInstallStatus.failed;
          });
          slsLogger.log({
            key : "client",
            action : "install_timeout",
            arrt : "McpQuery",
            count : +new Date - beginTime,
            ext2 : server_name,
            ext3 : JSON.stringify(this.serverList),
          });
          resolve({
            code : 1,
            msg : "调用客户端接口超时",
          });
        }, timeout * 3);

        client.proxyRequestAsync("McpQuery", {
          api : "/api/server/install/batch",
          method : 0,
          timeout, 
          param : {
            server_name,
          }
        }, "", "360Mcp")
        .then(res => {
          // TODO 临时添加上报
          slsLogger.log({
            key: 'client',
            action: 'install_batch',
            arrt: 'McpQuery',
            count: +new Date - beginTime,
            ext: res.data,
            ext2: server_name,
            ext3: JSON.stringify(this.serverList),
          })
          
          if(hasTimeout){
            return;
          }

          runInAction(() => {
            const server = this.serverList.find(item => item.server_name == server_name);
            if (!server) return
            try{
              const { code, data } = JSON.parse(res.data);
              if(code != 0){
                server.status = McpServerInstallStatus.failed;
              }
              else {
                data.forEach(item => {
                  if(item.server_name == server_name){
                    server.status = item.installed ? McpServerInstallStatus.installed : McpServerInstallStatus.failed;
                    if(item.installed){
                      clientMcp.setMcpInstallCache(server_name);
                    }
                  }
                })
              }
            } catch(e){
              slsLogger.log({
                key : "client",
                action : "install_batch_error",
                arrt : "McpQuery",
                count : +new Date - beginTime,
                ext: e.stack || e.toString(),
                ext2 : server_name,
                ext3 : JSON.stringify(this.serverList),
              })
              console.error(`安装 MCP 服务列表 Error:`, e);
              server.status = McpServerInstallStatus.failed;
            }

            clearTimeout(timer);
            resolve({
              code : 0,
              msg : "调用客户端接口成功",
            });
          });
        });
      })
    }))
    .finally(() => {
      console.info('>>> 安装 MCP 服务列表完成')

      slsLogger.log({
        key : "client",
        action : "install_over",
        count : +new Date - beginTime,
        ext2 : server_names.join(","),
        ext3 : JSON.stringify(this.serverList),
      });

      runInAction(() => {
        // 有可用的 mcp server
        if(this.serverList.some(item => item.is_local != 1 || item.status == McpServerInstallStatus.installed)){
          this.installed = true;
        }
      })
    })
  }
}

const chatAgent = new ChatAgent()
export default chatAgent
